import React from 'react';
import dayjs from 'dayjs';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
} from '@headlessui/react';
import { DaySequenceStatus } from '@core/generated/gql-types';
import type { TimesheetApprovalItem } from '@core/timesheetApproval/timesheetApproval.types';
import Button from '@/components/Button';
import { formatTimesheetDateRange } from '@/modules/timesheets/utils/timesheetGroups.utils';
import { formatBreakTime, formatTimeRangeAndDuration } from '@/utils/utils';

interface ApproveTimesheetsModalProps {
  isOpen: boolean;
  workerName: string;
  startDateISO: string;
  endDateISO: string;
  items: TimesheetApprovalItem[];
  showClientName: boolean;
  isApproving: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const ApproveTimesheetsModal = ({
  isOpen,
  workerName,
  startDateISO,
  endDateISO,
  items,
  showClientName,
  isApproving,
  onClose,
  onConfirm,
}: ApproveTimesheetsModalProps) => {
  const closeUnlessApproving = () => {
    if (!isApproving) onClose();
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-[60]"
        onClose={closeUnlessApproving}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
                <div className="border-b border-gray-200 px-6 py-5">
                  <DialogTitle
                    as="h3"
                    className="text-lg font-semibold text-gray-900"
                  >
                    Approve timesheets?
                  </DialogTitle>
                  <p className="mt-1 text-sm text-gray-600">
                    {workerName} ·{' '}
                    {formatTimesheetDateRange(startDateISO, endDateISO)}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Review all {items.length}{' '}
                    {items.length === 1 ? 'timesheet' : 'timesheets'} before
                    approving this pay cycle.
                  </p>
                </div>

                <div className="space-y-3 overflow-y-auto bg-gray-50 px-6 py-4">
                  {items.map((item) => {
                    const { daySequence } = item;
                    const hasProposedChange =
                      daySequence.status === DaySequenceStatus.ProposedChange;
                    const clockIn = hasProposedChange
                      ? daySequence.proposedClockInTime
                      : daySequence.clockInTime;
                    const clockOut = hasProposedChange
                      ? daySequence.proposedClockOutTime
                      : daySequence.clockOutTime;
                    const breaks = hasProposedChange
                      ? daySequence.proposedBreaks
                      : daySequence.breaks;
                    const location =
                      [showClientName ? item.clientName : null, item.siteName]
                        .filter(Boolean)
                        .join(' · ') || '—';

                    return (
                      <div
                        key={daySequence.id}
                        className="rounded-lg border border-gray-200 bg-white p-4"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="max-w-2/3 min-w-0">
                            <p className="text-sm font-semibold whitespace-nowrap text-gray-900">
                              {daySequence.startDateISO
                                ? dayjs(daySequence.startDateISO).format(
                                    'ddd, D MMM',
                                  )
                                : '—'}
                            </p>
                            <p className="text-sm text-gray-600">
                              {item.jobTitle || '—'}
                            </p>
                            <p className="text-xs text-gray-500">{location}</p>
                          </div>
                          {hasProposedChange && (
                            <span className="rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800">
                              Proposed change
                            </span>
                          )}
                        </div>
                        <div className="mt-3 grid gap-1 text-sm sm:grid-cols-2 sm:gap-4">
                          <p>
                            <span className="text-gray-500">Time: </span>
                            <span className="text-gray-900">
                              {formatTimeRangeAndDuration(
                                clockIn,
                                clockOut,
                                item.jobTimezone,
                              )}
                            </span>
                          </p>
                          <p>
                            <span className="text-gray-500">Break: </span>
                            <span className="text-gray-900">
                              {formatBreakTime(breaks || [])}
                            </span>
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isApproving}
                    onClick={closeUnlessApproving}
                    className="!w-auto !px-4 !py-2 !text-sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    isLoading={isApproving}
                    onClick={onConfirm}
                    className="!w-auto !px-4 !py-2 !text-sm"
                  >
                    Approve {items.length}{' '}
                    {items.length === 1 ? 'timesheet' : 'timesheets'}
                  </Button>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ApproveTimesheetsModal;
